import { Tabs } from 'dig-components/tabs';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import DocumentsSearch from 'hellospa/components/docs/search';
import { Dispatch } from 'hellospa/redux/types';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionSource,
  clearTemplateSearch,
  searchTemplatesThunk,
  selectCurrentTab,
  selectTemplateSearch,
  setTab,
  TemplatePageTab,
} from './template-slice';
import styles from './index.scss';
import classNames from 'classnames';
import { trackHeapCustomEvent } from 'common/utils/heap';
import { Link, Text } from 'dig-components/typography';
import { useHSUser } from 'hellospa/common/hs-user';

type Props = {
  isSearching: boolean,
  includeTemplateLinks: boolean,
  setIsSearching: (searching: boolean) => void,
  isLoading: boolean,
}

const messages = defineMessages({
  searchResults: {
    id: '',
    description: 'Search results count',
    defaultMessage: '{resultCount, plural, one {# result} few {# results} many {# results} other {# results}}',
  },
  clearSearchResults: {
    id: '',
    description: 'Clear search results link text',
    defaultMessage: 'Clear search',
  },
});

export function TabsAndSearch({
  isSearching,
  setIsSearching,
  includeTemplateLinks,
  isLoading,
}: Props) {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const search = useSelector(selectTemplateSearch);
  const currentTab = useSelector(selectCurrentTab);
  const currentQuery = search?.query ?? '';
  const resultCount = search?.resultsCount ?? null;
  const [searchField, setSearchField] = React.useState<null | HTMLElement>(null);
  const clearSearch = () => {
    dispatch(clearTemplateSearch());
    if (searchField) {
      searchField.focus();
    }
  };
  const { isFree } = useHSUser();
  const onSelection = useCallback((tab: string) => {
    dispatch(setTab({ tab: tab as TemplatePageTab, isFree, source: ActionSource.User }));
  }, [dispatch, isFree]);

  return (
    <Tabs
      className={classNames(styles.tabsAndSearch, {
        [styles.isSearching]: isSearching,
      })}
      selectedTab={currentTab}
      onSelection={onSelection}
      onClick={(event) => {
        if ((event.target as HTMLDivElement).id) {
          trackHeapCustomEvent('click_tab_templates_page', {
            'Tab Name': (event.target as HTMLDivElement).id,
          });
        }
      }}
    >
      <div className={styles.tabContainer}>
        {!isSearching && (
        <Tabs.Group >
          <Tabs.Tab id="starred">
            <FormattedMessage
              id=""
              description="Name for a button which will show the user's starred templates"
              defaultMessage="Starred" />
          </Tabs.Tab>
          <Tabs.Tab id="all">
            <FormattedMessage
              id=""
              description="Name for a button which will show the user's templates."
              defaultMessage="All templates" />
          </Tabs.Tab>
        </Tabs.Group>
        )}
        <DocumentsSearch
          data-qa-ref="template-search"
          currentValue={currentQuery}
          className={classNames(styles.search, { [styles.relative]: isSearching })}
          isOpen={isSearching}
          onSearch={(query) => {
            dispatch(searchTemplatesThunk({ pageNum: 1, query, includeTemplateLinks }));
          }}
          onFocus={() => setIsSearching(true)}
          onClick={(el) => {
            if ((el.target as HTMLElement).tagName === 'INPUT') {
              setSearchField((el.target as HTMLElement));
            }
          }}
          onClosePressed={() => setIsSearching(false)}
          requestShowAdvancedSearchModal={() => { /* TODO */ }} />
      </div>
      {!isLoading && resultCount !== null && search?.status === 'fulfilled'
        && <div>
          <Spacer top={SpacerSize.XXL}>
            <Text
              variant='label'
              size='xlarge'
              data-qa-ref="template-search-result-count"
              isBold
            >
              {intl.formatMessage(messages.searchResults, { resultCount })}
            </Text>
            &nbsp;
            <Link
              className={styles.clearSearch}
              data-qa-ref="template-search-clear-search"
              variant='monochromatic'
              href="#"
              onClick={() => clearSearch()}
              hasNoUnderline
              isBold
            >
              {intl.formatMessage(messages.clearSearchResults)}
            </Link>
          </Spacer>
        </div>
      }
    </Tabs>
  );
}
